"use client";

import React, { useState } from "react";
import { ComponentConfig } from "@puckeditor/core";
import { businessData } from "@/data/business";

export interface RegItem {
  code: string;
  title: string;
  desc: string;
}

export interface ActiveItem {
  name: string;
  category: string;
  optimumPh: string;
  maxLoad: string;
  benefits: string;
}

export interface RegulatoryMatrixProps {
  heading?: string;
  subheading?: string;
  frameworks?: RegItem[];
  actives?: ActiveItem[];
}

export const RegulatoryMatrixBlock: ComponentConfig<RegulatoryMatrixProps> = {
  label: "Regulatory & Actives Matrix",
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    frameworks: {
      type: "array",
      getItemSummary: (r: any) => r.code || "Framework",
      arrayFields: {
        code: { type: "text", label: "Framework Badge Code" },
        title: { type: "text", label: "Title" },
        desc: { type: "textarea", label: "Description" },
      },
      defaultItemProps: {
        code: "BATCH QC",
        title: "Specification and release controls",
        desc: "Describe the applicable standard, test method, acceptance criteria, and document supplied with the batch.",
      },
    },
    actives: {
      type: "array",
      getItemSummary: (a: any) => a.name || "Active Ingredient",
      arrayFields: {
        name: { type: "text", label: "Ingredient Name" },
        category: { type: "text", label: "Category" },
        optimumPh: { type: "text", label: "Optimum pH Range" },
        maxLoad: { type: "text", label: "Max Load %" },
        benefits: { type: "textarea", label: "Benefits Description" },
      },
      defaultItemProps: {
        name: "Niacinamide",
        category: "Barrier support and sebum care",
        optimumPh: "5.0–6.0",
        maxLoad: "Up to 10%",
        benefits: "Define the intended function, supported concentration, compatibility limits, and claim language after formulation review.",
      },
    },
  },
  defaultProps: {
    heading: "Regulatory Dossiers & Bio-Active Standards",
    subheading: "Review our compliance frameworks and proven active carrier compatibility matrix.",
    frameworks: businessData.regulatory,
    actives: businessData.actives,
  },
  render: function RegulatoryMatrix({ heading, subheading, frameworks, actives }) {
    const [activeTab, setActiveTab] = useState<"regulatory" | "actives">("regulatory");

    const regList = frameworks && frameworks.length > 0 ? frameworks : businessData.regulatory;
    const activeList = actives && actives.length > 0 ? actives : businessData.actives;

    return (
      <section className="sec" id="compliance">
        <div className="container">
          <div className="sec-head">
            <h2>{heading}</h2>
            <p className="sec-sub">{subheading}</p>
          </div>

          <div className="matrix-tabs">
            <button
              type="button"
              className={`matrix-tab ${activeTab === "regulatory" ? "active" : ""}`}
              onClick={() => setActiveTab("regulatory")}
            >
              Regulatory Frameworks
            </button>
            <button
              type="button"
              className={`matrix-tab ${activeTab === "actives" ? "active" : ""}`}
              onClick={() => setActiveTab("actives")}
            >
              Bio-Active Compatibility Library
            </button>
          </div>

          {activeTab === "regulatory" ? (
            <div className="reg-grid">
              {regList.map((item, idx) => (
                <div className="reg-card" data-animate key={idx}>
                  <div className="reg-code">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <polyline points="9 12 11 14 15 10" />
                    </svg>
                  </div>
                  <div>
                    <span className="reg-meta">{item.code}</span>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="actives-grid">
              {activeList.map((act, idx) => (
                <div className="active-card" data-animate key={idx}>
                  <div className="active-header">
                    <h3>{act.name}</h3>
                    <span className="active-tag">{act.maxLoad}</span>
                  </div>
                  <div className="active-meta">
                    <span>pH {act.optimumPh}</span>
                    <span>•</span>
                    <span>{act.category}</span>
                  </div>
                  <p>{act.benefits}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  },
};
